/**
 * transitionLoaderStore — Zustand store that controls the full-screen
 * CockpitLoader overlay used during route transitions (login → dashboard,
 * navigation between authenticated pages, etc.).
 *
 * Pattern:
 *   const show = useTransitionLoader((s) => s.show);
 *   show(() => navigate('/dashboard'));
 *
 * The overlay mounts when `isVisible` flips true, runs its ~1500ms
 * animation, then on completion calls the queued `onComplete` callback
 * (which is where the actual navigation happens). The store then flips
 * `isVisible` back to false to unmount.
 *
 * Why a callback rather than just navigating before showing the overlay:
 * we want the overlay to cover the route transition, so the navigation
 * fires INSIDE the loader's onComplete (after the brand animation has
 * played) and the destination page renders behind the fading overlay.
 *
 * `variant` is optional — leave undefined for the random pick (the default
 * brand experience). Pass 0-4 to force a specific variant for tests.
 */

import { create } from 'zustand';

interface TransitionLoaderState {
  isVisible: boolean;
  variant?: number;
  /** Fires when the loader animation completes (use to navigate). */
  onComplete: (() => void) | null;
  /**
   * Show the loader. If a callback is passed, it runs when the animation
   * completes (typically: navigate to the next page). The overlay then
   * unmounts. Calling show() while already visible replaces the pending
   * callback — useful if a faster click should win.
   */
  show: (onComplete?: () => void, variant?: number) => void;
  /** Hide immediately without firing onComplete. Used internally. */
  hide: () => void;
  /** Internal — called by CockpitLoader when its animation finishes. */
  complete: () => void;
}

export const useTransitionLoader = create<TransitionLoaderState>((set, get) => ({
  isVisible: false,
  variant: undefined,
  onComplete: null,

  show: (onComplete, variant) =>
    set({ isVisible: true, onComplete: onComplete ?? null, variant }),

  hide: () => set({ isVisible: false, onComplete: null, variant: undefined }),

  complete: () => {
    const cb = get().onComplete;
    // Unmount first so the next page renders cleanly, then fire the
    // navigation callback. The CockpitLoader's own fade-out has already
    // run by the time complete() is called.
    set({ isVisible: false, onComplete: null, variant: undefined });
    if (cb) {cb();}
  },
}));
